/**
 * Structured client-side logger for LAUNCHER-2.0.
 *
 * - JSON output in production, readable output in development.
 * - Controlled via VITE_LOG_LEVEL env var (error | warn | info | debug).
 * - Every entry includes timestamp, component, and event type.
 */

const LEVELS = { error: 0, warn: 1, info: 2, debug: 3 };

const configuredLevel =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LOG_LEVEL) || 'info';

const currentLevel = LEVELS[configuredLevel] ?? LEVELS.info;
const isProduction =
  typeof import.meta !== 'undefined' && import.meta.env?.PROD;

function formatEntry(level, component, event, data) {
  const entry = {
    ts: new Date().toISOString(),
    level,
    component,
    event,
    ...data,
  };
  return entry;
}

function emit(level, component, event, data = {}) {
  if (LEVELS[level] > currentLevel) return;

  const entry = formatEntry(level, component, event, data);

  if (isProduction) {
    // Structured JSON for log aggregation
    const json = JSON.stringify(entry);
    if (level === 'error') console.error(json);
    else if (level === 'warn') console.warn(json);
    else console.log(json);
  } else {
    // Human-readable in dev
    const tag = `[${entry.ts.slice(11, 23)}] [${level.toUpperCase()}] [${component}]`;
    const rest = { ...data };
    const hasData = Object.keys(rest).length > 0;
    if (level === 'error') console.error(tag, event, ...(hasData ? [rest] : []));
    else if (level === 'warn') console.warn(tag, event, ...(hasData ? [rest] : []));
    else if (level === 'debug') console.debug(tag, event, ...(hasData ? [rest] : []));
    else console.log(tag, event, ...(hasData ? [rest] : []));
  }

  return entry;
}

/**
 * Create a scoped logger for a given component.
 *
 * @param {string} component — e.g. 'ConnectionContext', 'ChatPanel'
 * @returns {{ error, warn, info, debug }}
 */
export function createLogger(component) {
  return {
    error: (event, data) => emit('error', component, event, data),
    warn:  (event, data) => emit('warn',  component, event, data),
    info:  (event, data) => emit('info',  component, event, data),
    debug: (event, data) => emit('debug', component, event, data),
  };
}

export default createLogger;
